import { api } from './index';

export enum FeePricingType {
  SINGLE_TIER = 'SINGLE_TIER',
  TIERED = 'TIERED',
  QUOTA_BASED = 'QUOTA_BASED'
}

export enum FeeQuantitySource {
  MANUAL = 'MANUAL',
  APARTMENT_AREA = 'APARTMENT_AREA',
  FIXED_ONE = 'FIXED_ONE',
}

export interface CompositePricingFieldsData {
  extraFee1Percent?: number;
  extraFee1Amount?: number | null;
  extraFee1VatPercent?: number;
  extraFee1VatAmount?: number | null;
  extraFee1Fixed?: number;
  extraFee1Name?: string | null;
  extraFee2Percent?: number;
  extraFee2Amount?: number | null;
  extraFee2VatPercent?: number;
  extraFee2VatAmount?: number | null;
  extraFee2Fixed?: number;
  extraFee2Name?: string | null;
  totalUnitPrice?: number | null;
}

export interface FeeTier extends CompositePricingFieldsData {
  id: string;
  tierLevel: number;
  usageFrom?: number;
  usageTo?: number;
  price: number;
  basePrice?: number | null;
  vatAmount?: number | null;
  unit?: string;
}

export interface FeeTierData extends CompositePricingFieldsData {
  tierLevel: number;
  usageFrom?: number | string;
  usageTo?: number | string | null;
  price: number | string;
  basePrice?: number | null;
  vatAmount?: number | null;
  unit?: string;
}

export interface FeeCategory extends CompositePricingFieldsData {
  id: string;
  name: string;
  feeCode?: string;
  buildingId: string;
  pricingType: FeePricingType;
  basePrice?: number;
  vatPercentage: number;
  vatAmount?: number | null;
  isMonthlyFixed: boolean;
  autoCloseMonthly: boolean;
  billingTiming?: 'PREPAID' | 'POSTPAID';
  isActive: boolean;
  notes?: string;
  waiveFirstVehicleCardIssuanceFee?: boolean;
  quantitySource?: FeeQuantitySource;
  quantityUnit?: string | null;
  
  // Quota-based pricing fields
  quotaLimit?: number;
  quotaPrice?: number;
  overQuotaPrice1?: number;
  overQuotaPrice2?: number;
  wastagePercentage?: number;
  environmentalFee?: number;
  quotaCalculationType?: string;
  
  // Base prices for quota-based pricing (without VAT and environmental fees)
  baseQuotaPrice?: number;
  baseOverQuotaPrice1?: number;
  baseOverQuotaPrice2?: number;
  
  createdAt: string;
  updatedAt: string;
  building: {
    id: string;
    name: string;
  };
  feeTiers: FeeTier[];
}

export interface CreateFeeCategoryData extends CompositePricingFieldsData {
  name: string;
  feeCode?: string | null;
  buildingId: string;
  pricingType: FeePricingType;
  basePrice?: number;
  vatPercentage: number;
  vatAmount?: number | null;
  isMonthlyFixed: boolean;
  autoCloseMonthly: boolean;
  billingTiming?: 'PREPAID' | 'POSTPAID';
  isActive?: boolean;
  notes?: string;
  waiveFirstVehicleCardIssuanceFee?: boolean;
  quantitySource?: FeeQuantitySource;
  quantityUnit?: string | null;
  
  // Quota-based pricing fields
  quotaLimit?: number;
  quotaPrice?: number;
  overQuotaPrice1?: number;
  overQuotaPrice2?: number;
  wastagePercentage?: number;
  environmentalFee?: number;
  quotaCalculationType?: string;
  
  // Base prices for quota-based pricing (without VAT and environmental fees)
  baseQuotaPrice?: number;
  baseOverQuotaPrice1?: number;
  baseOverQuotaPrice2?: number;
  
  feeTiers?: FeeTierData[];
}

export interface UpdateFeeCategoryData extends Partial<CreateFeeCategoryData> {}

export interface BuildingOption {
  id: string;
  name: string;
  operatorId: string;
  operator: {
    id: string;
    name: string;
    email: string;
  };
}

export interface UsageBasedFeeService {
  id: string;
  name: string;
  buildingId: string;
  pricingType: FeePricingType;
  vatPercentage: number;
  feeTiers: FeeTier[];
}

export interface BillingPeriod {
  month: number;
  year: number;
  label: string;
}

export interface ApartmentReading {
  id: string;
  apartmentCode: string;
  apartmentName: string;
  oldReading?: number;
  newReading?: number;
  usage?: number;
}

export interface BulkFeeEntry {
  serviceId: string;
  buildingId: string;
  billingMonth: number;
  billingYear: number;
  readings: ApartmentReading[];
}

export interface FeeCategoryImportResult {
  success: boolean;
  message: string;
  importedCount: number;
  updatedCount: number;
  skippedCount: number;
  skippedCodes: string[];
  errors: string[];
}

export const feeCategoriesApi = {
  // Get all fee categories for a building
  async getAll(buildingId: string): Promise<FeeCategory[]> {
    const response = await api.get(`/fee-categories?buildingId=${buildingId}`);
    return response.data;
  },

  // Get fee category by ID
  async getById(id: string): Promise<FeeCategory> {
    const response = await api.get(`/fee-categories/${id}`);
    return response.data;
  },

  // Create new fee category
  async create(data: CreateFeeCategoryData): Promise<FeeCategory> {
    const response = await api.post('/fee-categories', data);
    return response.data;
  },

  // Update fee category
  async update(id: string, data: UpdateFeeCategoryData): Promise<FeeCategory> {
    const response = await api.patch(`/fee-categories/${id}`, data);
    return response.data;
  },

  // Update fee category status
  async updateStatus(id: string, isActive: boolean): Promise<FeeCategory> {
    const response = await api.patch(`/fee-categories/${id}`, { isActive });
    return response.data;
  },

  // Delete fee category
  async delete(id: string): Promise<{ message: string }> {
    const response = await api.delete(`/fee-categories/${id}`);
    return response.data;
  },

  // Get buildings for current user
  async getBuildingsForUser(): Promise<BuildingOption[]> {
    const response = await api.get('/fee-categories/buildings');
    return response.data;
  },

  // Create default fee categories for a building
  async seedDefault(buildingId: string, append = false): Promise<{ message: string; created?: number }> {
    const response = await api.post(`/fee-categories/seed-default`, { buildingId, append });
    return response.data;
  },

  async exportCategories(buildingId: string): Promise<void> {
    const response = await api.get(`/fee-categories/export?buildingId=${buildingId}`, {
      responseType: 'blob',
    });

    const disposition = response.headers['content-disposition'] as string | undefined;
    const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] || `fee-categories-export-${new Date().toISOString().slice(0, 10)}.json`;

    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  async importCategories(
    buildingId: string,
    file: File,
    mode: 'skip' | 'replace' = 'skip',
  ): Promise<FeeCategoryImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('buildingId', buildingId);
    formData.append('mode', mode);

    const response = await api.post('/fee-categories/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Get usage-based fee services for a building (exclude fixed monthly fees)
  async getUsageBasedServices(buildingId: string): Promise<UsageBasedFeeService[]> {
    const response = await api.get(`/fee-categories/usage-based?buildingId=${buildingId}`);
    return response.data;
  },

  // Get recent billing periods (last 3 months)
  async getBillingPeriods(): Promise<BillingPeriod[]> {
    const response = await api.get('/billing/periods');
    return response.data;
  },

  // Get apartments for a building with latest readings
  async getApartmentsWithReadings(buildingId: string, serviceId?: string): Promise<ApartmentReading[]> {
    const response = await api.get(`/apartments/readings?buildingId=${buildingId}${serviceId ? `&serviceId=${serviceId}` : ''}`);
    return response.data;
  },

  // Submit bulk fee entry
  async submitBulkFeeEntry(data: BulkFeeEntry): Promise<{ message: string }> {
    const response = await api.post('/billing/bulk-entry', data);
    return response.data;
  },

  // Export sample Excel file
  async exportSampleFile(buildingId: string): Promise<Blob> {
    const response = await api.get(`/billing/export-sample?buildingId=${buildingId}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Import data from Excel file
  async importFromFile(buildingId: string, serviceId: string, file: File): Promise<ApartmentReading[]> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('buildingId', buildingId);
    formData.append('serviceId', serviceId);
    
    const response = await api.post('/billing/import-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Calculate quota-based fee
  async calculateQuotaBasedFee(feeCategoryId: string, usage: number, userId: string, userRole: string, apartmentId?: string): Promise<any> {
    const response = await api.post(`/fee-categories/${feeCategoryId}/calculate-quota`, {
      usage,
      userId,
      userRole,
      apartmentId
    });
    return response.data;
  },

  async calculateTieredFee(
    feeCategoryId: string,
    usage: number,
    apartmentId?: string,
    utilityMeterId?: string,
  ): Promise<{
    baseAmount: number;
    vatAmount: number;
    totalAmount: number;
    registeredQuotaCount: number;
    quotaTierStart?: string;
    billingMode?: string;
    scaledTiers: Array<{
      tierLevel: number;
      usageFrom: number;
      usageTo: number | null;
      usageInTier: number;
      unitPrice: number;
      amount: number;
    }>;
  }> {
    const response = await api.post(`/fee-categories/${feeCategoryId}/calculate-tiered`, {
      usage,
      apartmentId,
      utilityMeterId,
    });
    return response.data;
  },
}; 